import React from "react";
import { Check } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const OFFICE_IMG =
  "https://images.unsplash.com/photo-1724906019868-93ad2c79414f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc5NjI0NjI2fDA&ixlib=rb-4.1.0&q=85";

export const ValueProp = () => {
  const { t } = useLanguage();

  return (
    <section
      id="value"
      data-testid="value-section"
      className="relative py-24 md:py-32 border-t border-slate-800 bg-[#0B1221]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: text */}
          <div className="lg:col-span-7">
            <p
              className="text-[11px] tracking-[0.2em] uppercase text-blue-400 mb-5"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {t.value.overline}
            </p>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight text-slate-50 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="value-title"
            >
              {t.value.title}
            </h2>
            <p
              className="mt-5 text-base md:text-lg text-slate-400 max-w-2xl"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {t.value.subtitle}
            </p>

            <ul className="mt-10 space-y-6">
              {t.value.points.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 pb-6 border-b border-slate-800 last:border-b-0"
                  data-testid={`value-point-${i}`}
                >
                  <span className="mt-1 inline-flex items-center justify-center w-6 h-6 border border-blue-500/40 bg-blue-500/10 text-blue-400 shrink-0">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <div>
                    <h3
                      className="text-base md:text-lg font-semibold text-slate-50"
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {p.title}
                    </h3>
                    <p
                      className="mt-1 text-sm md:text-base text-slate-400"
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      {p.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: image with grid overlay */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-slate-800">
              <img
                src={OFFICE_IMG}
                alt="Modern corporate office architecture"
                className="w-full h-full object-cover grayscale opacity-90"
              />
              <div
                className="absolute inset-0 mix-blend-overlay"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(15,23,42,0.2) 0%, rgba(37,99,235,0.25) 100%)",
                }}
                aria-hidden="true"
              />
              <div
                className="absolute inset-0 pointer-events-none opacity-30"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #93c5fd 1px, transparent 1px), linear-gradient(to bottom, #93c5fd 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
                aria-hidden="true"
              />
              <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-slate-950 to-transparent">
                <p
                  className="text-[10px] tracking-[0.25em] uppercase text-blue-300"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  {t.common.valueOverlay}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueProp;
